import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Speed as SpeedIcon,
  Description as DescriptionIcon,
  Psychology as PsychologyIcon,
  TrendingUp as TrendingUpIcon,
  Groups as GroupsIcon,
  Phone as PhoneIcon,
  ArrowBack as ArrowBackIcon,
  Verified as VerifiedIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import { keyframes } from '@mui/system';

// Premium color palette (matching PrimesLandingPage)
const colors = {
  primaryDark: '#0D1B2A',
  primaryMid: '#1B263B',
  primaryLight: '#415A77',
  accent: '#FF6B35',
  accentLight: '#FF8C5A',
  accentGlow: 'rgba(255, 107, 53, 0.3)',
  teal: '#00B4D8',
  tealLight: '#48CAE4',
  tealGlow: 'rgba(0, 180, 216, 0.2)',
  white: '#FFFFFF',
  offWhite: '#F8F9FA',
  lightGray: '#E9ECEF',
  midGray: '#778DA9',
  darkGray: '#415A77',
  success: '#10B981',
  error: '#EF4444',
  warning: '#F59E0B',
};

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

/**
 * WhyPrimesPage - "Why Primes Work With REDEX Labs" One-Pager
 * 
 * Deep-dive explanation of REDEX's value proposition for prime contractors
 */
const WhyPrimesPage: React.FC = () => {
  const navigate = useNavigate();
  const BOOKING_LINK = 'https://calendar.google.com/calendar/appointments/schedules/AcZssZ0N2nccvrbmxTCwRMt_Xiw_SZ6KHGvGA6JCi39FmeLQS_ZFPizhD-LrfsQrzOxeOKCv1Uj_LY5X?gv=true';

  const handleBookCall = () => {
    window.open(BOOKING_LINK, '_blank', 'noopener,noreferrer');
  };

  const problems = [
    {
      title: 'Delivery Risk',
      description: 'Technical work falls behind schedule, threatening contract performance',
    },
    {
      title: 'Compliance Burden',
      description: 'Documentation gaps and audit findings create overhead and exposure',
    },
    {
      title: 'Talent Shortage',
      description: 'Difficulty finding qualified engineers at competitive rates',
    },
    {
      title: 'Subcontracting Goals',
      description: 'Need to meet small business utilization while maintaining quality',
    },
  ];

  const solutions = [
    {
      icon: <PsychologyIcon sx={{ fontSize: 28 }} />,
      title: 'Research-Backed Development',
      gradient: `linear-gradient(135deg, ${colors.teal} 0%, ${colors.tealLight} 100%)`,
      points: [
        'Modern engineering practices with documented methodologies',
        'Version-controlled code with comprehensive test coverage',
        'Architecture decisions documented and justified',
        'Code reviews and quality gates at every stage',
      ],
    },
    {
      icon: <DescriptionIcon sx={{ fontSize: 28 }} />,
      title: 'Audit-Ready Documentation',
      gradient: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentLight} 100%)`,
      points: [
        'System design documents and technical specifications',
        'User guides and operational runbooks',
        'Change logs and decision records',
        'Compliance evidence packages for agency review',
      ],
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 28 }} />,
      title: 'Rapid, Transparent Execution',
      gradient: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
      points: [
        'Weekly demos showing tangible progress',
        'Real-time project dashboards and burn rates',
        'Proactive risk identification and mitigation',
        'Flexible resourcing to meet deadlines',
      ],
    },
  ];

  const differentiators = [
    {
      category: 'Delivery Speed',
      traditional: 'Weeks to months for onboarding',
      redex: 'Under 2 weeks to productive capacity',
    },
    {
      category: 'Documentation',
      traditional: 'Often incomplete or after-the-fact',
      redex: 'Concurrent, audit-ready from day one',
    },
    {
      category: 'Communication',
      traditional: 'Monthly status calls',
      redex: 'Weekly demos + async updates',
    },
    {
      category: 'Risk Management',
      traditional: 'Issues surface late',
      redex: 'Proactive identification and escalation',
    },
    {
      category: 'Flexibility',
      traditional: 'Rigid contracts',
      redex: 'Scalable capacity on demand',
    },
  ];

  const metrics = [
    { value: '< 2 Weeks', label: 'Onboarding Time' },
    { value: '100%', label: 'Documentation Coverage' },
    { value: 'Weekly', label: 'Demo Cadence' },
    { value: 'Audit-Ready', label: 'All Deliverables' },
  ];

  const engagementTable = [
    {
      model: 'Retainer Partnership',
      bestFor: 'Ongoing technical capacity needs',
      commitment: 'Monthly recurring',
      billing: 'Fixed monthly + hourly overages',
    },
    {
      model: 'Project Delivery',
      bestFor: 'Defined scope deliverables',
      commitment: 'Project duration',
      billing: 'Milestone-based',
    },
    {
      model: 'Staff Augmentation',
      bestFor: 'Specific skill gaps',
      commitment: 'Flexible term',
      billing: 'Hourly',
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(180deg, ${colors.primaryDark} 0%, ${colors.primaryMid} 50%, ${colors.primaryDark} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          right: '-15%',
          width: '50%',
          height: '50%',
          background: `radial-gradient(circle, ${colors.tealGlow} 0%, transparent 70%)`,
          filter: 'blur(100px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          left: '-10%',
          width: '40%',
          height: '40%',
          background: `radial-gradient(circle, ${colors.accentGlow} 0%, transparent 70%)`,
          filter: 'blur(100px)',
          pointerEvents: 'none',
        }}
      />

      {/* Navigation Header */}
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          background: `rgba(13, 27, 42, 0.9)`,
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid rgba(255, 255, 255, 0.08)`,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
            <Stack direction="row" spacing={3} alignItems="center">
              <Typography
                component="a"
                href="/landing.html"
                sx={{
                  fontWeight: 900,
                  fontSize: '1.5rem',
                  background: `linear-gradient(135deg, ${colors.teal} 0%, ${colors.accent} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textDecoration: 'none',
                }}
              >
                REDEX
              </Typography>
              <Chip
                label="PARTNER PROGRAM"
                size="small"
                sx={{
                  background: 'rgba(0, 180, 216, 0.15)',
                  color: colors.tealLight,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  letterSpacing: '0.05em',
                  border: `1px solid rgba(0, 180, 216, 0.3)`,
                }}
              />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                component={Link}
                to="/partners/primes"
                startIcon={<ArrowBackIcon />}
                sx={{ 
                  color: colors.midGray, 
                  textTransform: 'none',
                  '&:hover': { color: colors.white },
                }}
              >
                Partner Overview
              </Button>
              <Button
                variant="contained"
                onClick={handleBookCall}
                sx={{ 
                  background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentLight} 100%)`,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  boxShadow: `0 4px 20px ${colors.accentGlow}`,
                  '&:hover': {
                    boxShadow: `0 6px 30px ${colors.accentGlow}`,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Schedule Call
              </Button>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: 16, pb: 10 }}>
        {/* Hero Section */}
        <Box 
          sx={{ 
            textAlign: 'center', 
            mb: 10,
            animation: `${fadeInUp} 0.8s ease-out`,
          }}
        >
          <Chip
            label="PARTNERSHIP DEEP-DIVE"
            sx={{
              mb: 4,
              py: 2,
              px: 1,
              background: 'rgba(0, 180, 216, 0.1)',
              border: `1px solid rgba(0, 180, 216, 0.3)`,
              color: colors.tealLight,
              fontWeight: 700,
              fontSize: '0.75rem',
              letterSpacing: '0.08em',
            }}
          />
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.25rem', md: '3.5rem' },
              fontWeight: 800,
              color: colors.white,
              mb: 3,
              lineHeight: 1.1,
            }}
          >
            Why Prime Contractors
            <br />
            <Box 
              component="span" 
              sx={{ 
                background: `linear-gradient(135deg, ${colors.teal} 0%, ${colors.tealLight} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Partner With REDEX Labs
            </Box>
          </Typography>
          <Typography
            sx={{
              color: colors.midGray,
              maxWidth: '700px',
              mx: 'auto',
              fontSize: '1.15rem',
              lineHeight: 1.7,
            }}
          >
            A deeper look at how we help federal contractors reduce delivery risk, 
            meet compliance requirements, and achieve small business utilization goals.
          </Typography>
        </Box>

        {/* Problem Statement */}
        <Box sx={{ mb: 12 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 700,
              color: colors.white,
              mb: 2,
              textAlign: 'center',
            }}
          >
            The Challenges Primes Face
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              color: colors.midGray,
              mb: 6,
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Common pain points we help solve
          </Typography>
          <Grid container spacing={3}>
            {problems.map((problem, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  sx={{
                    p: 3,
                    height: '100%',
                    background: 'rgba(239, 68, 68, 0.05)',
                    border: `1px solid rgba(239, 68, 68, 0.2)`,
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <WarningIcon sx={{ color: colors.error, mr: 1 }} />
                    <Typography sx={{ color: colors.white, fontWeight: 600 }}>
                      {problem.title}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: colors.midGray, fontSize: '0.9rem', lineHeight: 1.6 }}>
                    {problem.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Solutions */}
        <Box sx={{ mb: 12 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 700,
              color: colors.white,
              mb: 2,
              textAlign: 'center',
            }}
          >
            How REDEX Labs Solves These Challenges
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              color: colors.midGray,
              mb: 6,
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Our approach to risk reduction and quality delivery
          </Typography>
          <Grid container spacing={4}>
            {solutions.map((solution, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.02)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 16px 32px rgba(0, 0, 0, 0.2)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: solution.gradient,
                    }}
                  />
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: 2,
                        background: solution.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        color: colors.white,
                      }}
                    >
                      {solution.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{ color: colors.white, mb: 3, fontWeight: 600 }}
                    >
                      {solution.title}
                    </Typography>
                    <List dense disablePadding>
                      {solution.points.map((point, i) => (
                        <ListItem key={i} sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <CheckCircleIcon sx={{ fontSize: 16, color: colors.success }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={point}
                            primaryTypographyProps={{ 
                              color: colors.midGray, 
                              fontSize: '0.9rem',
                              lineHeight: 1.5,
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Metrics */}
        <Paper
          elevation={0}
          sx={{
            background: 'rgba(0, 180, 216, 0.08)',
            borderRadius: 4,
            p: 5,
            mb: 12,
            border: `1px solid rgba(0, 180, 216, 0.2)`,
          }}
        >
          <Grid container spacing={4}>
            {metrics.map((metric, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    sx={{
                      fontSize: '2rem',
                      fontWeight: 800,
                      color: colors.tealLight,
                      mb: 0.5,
                    }}
                  >
                    {metric.value}
                  </Typography>
                  <Typography sx={{ color: colors.midGray, fontSize: '0.9rem' }}>
                    {metric.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Comparison Table */}
        <Box sx={{ mb: 12 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 700,
              color: colors.white,
              mb: 5,
              textAlign: 'center',
            }}
          >
            REDEX Labs vs Traditional Subcontractors
          </Typography>
          <TableContainer
            component={Paper}
            sx={{
              background: 'rgba(255, 255, 255, 0.02)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              border: '1px solid rgba(255, 255, 255, 0.08)',
              overflow: 'hidden',
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: colors.midGray, fontWeight: 600, borderColor: 'rgba(255,255,255,0.08)' }}>
                    Category
                  </TableCell>
                  <TableCell sx={{ color: colors.midGray, fontWeight: 600, borderColor: 'rgba(255,255,255,0.08)' }}>
                    Traditional Approach
                  </TableCell>
                  <TableCell sx={{ color: colors.tealLight, fontWeight: 600, borderColor: 'rgba(255,255,255,0.08)' }}>
                    REDEX Labs
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {differentiators.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ color: colors.white, fontWeight: 500, borderColor: 'rgba(255,255,255,0.05)' }}>
                      {row.category}
                    </TableCell>
                    <TableCell sx={{ color: colors.midGray, borderColor: 'rgba(255,255,255,0.05)' }}>
                      {row.traditional}
                    </TableCell>
                    <TableCell sx={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CheckCircleIcon sx={{ fontSize: 18, color: colors.success }} />
                        <Typography sx={{ color: colors.white }}>{row.redex}</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Engagement Options */}
        <Box sx={{ mb: 12 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 700,
              color: colors.white,
              mb: 5,
              textAlign: 'center',
            }}
          >
            Flexible Engagement Options
          </Typography>
          <TableContainer
            component={Paper}
            sx={{
              background: 'rgba(255, 255, 255, 0.02)',
              borderRadius: 3,
              border: '1px solid rgba(255, 255, 255, 0.08)',
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: colors.midGray, fontWeight: 600, borderColor: 'rgba(255,255,255,0.08)' }}>
                    Model
                  </TableCell>
                  <TableCell sx={{ color: colors.midGray, fontWeight: 600, borderColor: 'rgba(255,255,255,0.08)' }}>
                    Best For
                  </TableCell>
                  <TableCell sx={{ color: colors.midGray, fontWeight: 600, borderColor: 'rgba(255,255,255,0.08)' }}>
                    Commitment
                  </TableCell>
                  <TableCell sx={{ color: colors.midGray, fontWeight: 600, borderColor: 'rgba(255,255,255,0.08)' }}>
                    Billing
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {engagementTable.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ color: colors.tealLight, fontWeight: 600, borderColor: 'rgba(255,255,255,0.05)' }}>
                      {row.model}
                    </TableCell>
                    <TableCell sx={{ color: colors.white, borderColor: 'rgba(255,255,255,0.05)' }}>
                      {row.bestFor}
                    </TableCell>
                    <TableCell sx={{ color: colors.midGray, borderColor: 'rgba(255,255,255,0.05)' }}>
                      {row.commitment}
                    </TableCell>
                    <TableCell sx={{ color: colors.midGray, borderColor: 'rgba(255,255,255,0.05)' }}>
                      {row.billing}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Final CTA */}
        <Paper
          sx={{
            background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.teal} 100%)`,
            borderRadius: 4,
            p: { xs: 5, md: 8 },
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.15)',
            }}
          />
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              variant="h3"
              sx={{ 
                color: colors.white, 
                mb: 2, 
                fontWeight: 800,
                fontSize: { xs: '1.75rem', md: '2.5rem' },
              }}
            >
              Ready to Explore a Partnership?
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                mb: 5,
                maxWidth: '600px',
                mx: 'auto',
                fontSize: '1.1rem',
              }}
            >
              Schedule a 15-minute call to discuss your technical delivery needs 
              and explore how we can support your contracts.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleBookCall}
              startIcon={<ScheduleIcon />}
              sx={{
                py: 2,
                px: 6,
                fontSize: '1.15rem',
                fontWeight: 700,
                backgroundColor: colors.white,
                color: colors.primaryDark,
                borderRadius: 2,
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
                '&:hover': {
                  backgroundColor: colors.offWhite,
                  transform: 'translateY(-3px)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Book a Free Consultation
            </Button>
          </Box>
        </Paper>

        {/* Disclaimer */}
        <Paper
          sx={{
            mt: 8,
            p: 3,
            background: 'rgba(245, 158, 11, 0.05)',
            border: `1px solid rgba(245, 158, 11, 0.15)`,
            borderRadius: 2,
          }}
        >
          <Typography
            sx={{
              color: colors.midGray,
              fontSize: '0.8rem',
              lineHeight: 1.6,
            }}
          >
            <Box component="span" sx={{ color: colors.warning, fontWeight: 600 }}>Disclaimer:</Box>{' '}
            This content is for informational purposes and does not constitute legal, tax, or regulatory advice. 
            Information about small business subcontracting is general in nature — federal agencies do not mandate 
            subcontracting with specific firms. Primes select subcontractors based on their own evaluation of 
            qualifications, past performance, and contract requirements. REDEX Labs is a registered small business.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default WhyPrimesPage;
